import {
  isWeb,
  SizableText,
  styled,
  Text,
  YStack,
  YStackProps,
} from '@mythical/ui'
import { Tag } from 'app/components/primitive/Tag/Tag'
import { SrOnly } from 'app/components/primitive/SrOnly'
import React from 'react'
import { LinkCore } from 'solito/link'

import ContentThumbnail, { ContentThumbnailProps } from '../ContentThumbnail'

const CardWrapper = styled(YStack, {
  flexShrink: 1,
  space: '$2',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  alignItems: 'flex-start',
  width: '100%',
  variants: {
    horizontal: {
      true: {
        flexDirection: 'row',
      },
    },
  } as const,
})

const CardContent = styled(YStack, {
  space: '$2.5',
  width: '100%',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  alignItems: 'flex-start',
  variants: {
    size: {
      sm: {
        space: '$2',
      },
      md: {
        space: '$2',
      },
    },
    horizontal: {
      true: {
        flexShrink: 1,
        flexGrow: 1,
      },
    },
    textCenter: {
      true: {
        alignItems: 'center',
      },
    },
  } as const,
})

const CardHeading = styled(SizableText, {
  color: '$primary1',
  fontFamily: '$body',
  fontSize: '$5',
  variants: {
    size: {
      sm: {
        fontSize: '$2',
        $gtXs: {
          fontSize: '$3',
        },
        $gtSm: {
          fontSize: '$4',
        },
      },
      md: {
        fontSize: '$4',
        $gtXs: {
          fontSize: '$5',
        },
      },
    },
    textCenter: {
      true: {
        textAlign: 'center',
      },
    },
  } as const,
  defaultVariants: {
    size: 'md',
  },
})

const CardText = styled(Text, {
  color: '$secondary6',
  fontFamily: '$body',
  fontSize: '$2',
  variants: {
    size: {
      sm: {
        fontSize: '$1',
      },
      md: {
        fontSize: '$2',
      },
    },
    textCenter: {
      true: {
        textAlign: 'center',
      },
    },
  } as const,
})

export interface Poll {
  id: string
  questions: {
    id: string
    question: string
    answers: {
      id: string
      answer: string
      percent: number
      yourVote: boolean
    }[]
  }[]
  closingDate: string
}

export interface ContentCardProps {
  id: string
  image: string
  title: string
  datePublished: string
  duration?: string
  commentCount: number
  disableComments?: boolean
  category: string
  href: string
  slug: string
  poll?: Poll
  hidePoll?: boolean
  categoryHref?: string
  size?: 'sm' | 'md'
  smTextCenter?: boolean
  textCenter?: boolean
  horizontal?: boolean
  hideInfo?: boolean
  align?: 'left' | 'center'
  wrapperProps?: YStackProps
  thumbnailProps?: ContentThumbnailProps['wrapperProps']
  videoPost?: boolean
  secondsWatched?: number
  contentTier?: number | 'initiate'
  onNavigate?: () => void
}

const ContentCard = ({
  image,
  title,
  duration,
  datePublished,
  commentCount,
  disableComments,
  category,
  href,
  categoryHref,
  size = 'md',
  textCenter = false,
  horizontal = false,
  hideInfo = false,
  thumbnailProps,
  wrapperProps,
  videoPost = false,
  secondsWatched,
  onNavigate,
}: ContentCardProps) => {
  const commonTextProps = {
    size,
    textCenter,
  }

  return (
    <CardWrapper horizontal={horizontal} {...wrapperProps}>
      <LinkCore
        href={href}
        tabIndex={-1}
        Component={YStack}
        componentProps={{
          width: '100%',
          tag: 'a',
          onPress: onNavigate,
          mb: '$1',
          flexBasis: horizontal ? '45%' : 'auto',
          flexGrow: horizontal ? 0 : 1,
          pressStyle: {
            opacity: 0.7,
          },
          ...thumbnailProps,
        }}
      >
        <ContentThumbnail
          image={image}
          duration={duration}
          hidePlayButton={!videoPost}
          secondsWatched={secondsWatched}
        />
        {isWeb && <SrOnly>Open ${title}</SrOnly>}
      </LinkCore>

      <CardContent size={size} horizontal={horizontal} textCenter={textCenter}>
        {category?.length > 1 && categoryHref && (
          <LinkCore
            href={categoryHref}
            Component={Tag}
            componentProps={{
              tag: 'a',
              displaySize: 'sm',
              variant: 'secondary',
            }}
          >
            {category}
          </LinkCore>
        )}

        <LinkCore
          href={href}
          Component={YStack}
          componentProps={{
            tag: 'a',
            space: '$2',
            pressStyle: { opacity: 0.7 },
            hoverStyle: { opacity: 0.8 },
            onPress: onNavigate,
          }}
        >
          <CardHeading {...commonTextProps}>{title}</CardHeading>
          {!hideInfo && (
            <CardText {...commonTextProps}>
              {datePublished}{' '}
              {commentCount > 0 && !disableComments && (
                <>
                  •{' '}
                  <CardText {...commonTextProps} color="$secondary7">
                    {commentCount || '0'} COMMENTS
                  </CardText>
                </>
              )}
            </CardText>
          )}
        </LinkCore>
      </CardContent>
    </CardWrapper>
  )
}

export default ContentCard
